import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import ContentCard from './components/ContentCard';
import FilterBar from './components/FilterBar';
import StatsOverview from './components/StatsOverview';
import LeaderboardSection from './components/LeaderboardSection';

import { engagementService } from '../../services/engagementService';
import useContentStore from '../../store/contentStore';
import Icon from '../../components/AppIcon';


const HallOfFame = () => {
  const navigate = useNavigate();
  const [engagementMetrics, setEngagementMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  
  // Use centralized content store
  const { 
    contentItems, 
    fetchContent, 
    subscribeToChanges, 
    unsubscribeFromChanges 
  } = useContentStore();

  useEffect(() => {
    loadContent();
    
    // Subscribe to real-time updates
    subscribeToChanges();
    
    // Cleanup subscription on unmount
    return () => {
      unsubscribeFromChanges();
    };
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const [metricsData] = await Promise.all([
        engagementService?.getAll(),
        fetchContent() // Fetch from centralized store
      ]);
      setEngagementMetrics(metricsData);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Merge content with engagement metrics
  const contentWithMetrics = contentItems?.map(item => {
    const metrics = engagementMetrics?.find(m => m?.contentId === item?.id);
    return {
      ...item,
      views: metrics?.views || 0,
      clicks: metrics?.clicks || 0,
      likes: metrics?.interactions || 0
    };
  });

  const hallOfFameContent = contentWithMetrics?.filter(item => item?.rating >= 4.5);

  const filteredContent = hallOfFameContent
    ?.filter(item => selectedCategory === 'all' || item?.categoryId === selectedCategory)
    ?.filter(item => selectedType === 'all' || item?.type === selectedType)
    ?.sort((a, b) => {
      if (sortBy === 'rating') return b?.rating - a?.rating;
      if (sortBy === 'newest') return new Date(b?.uploadDate) - new Date(a?.uploadDate);
      if (sortBy === 'price-high') return (b?.price || 0) - (a?.price || 0);
      return 0;
    });

  const stats = {
    totalItems: hallOfFameContent?.length,
    avgRating: hallOfFameContent?.length > 0
      ? (hallOfFameContent?.reduce((sum, item) => sum + item?.rating, 0) / hallOfFameContent?.length)?.toFixed(1)
      : 0,
    topCategory: 'Filmes e Séries',
    totalViews: hallOfFameContent?.reduce((sum, item) => sum + (item?.views || 0), 0)
  };

  const handleWhatsAppRequest = (content) => {
    const message = `Olá UP MIDIA! Gostaria de solicitar: "${content?.title}"\n\nCategoria: ${content?.category}\nAvaliação: ${content?.rating}/5.0\n\nAguardo retorno!`;
    const whatsappUrl = `https://wa.me/5511999999999?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <title>Hall da Fama - UP MIDIA Gallery</title>
        <meta 
          name="description" 
          content="Descubra os conteúdos mais bem avaliados da UP MIDIA. Artes e vídeos premium com as melhores classificações e maior engajamento." 
        />
      </Helmet>
      <div className="min-h-screen bg-background pt-20">
        <div className="px-4 md:px-6 lg:px-10 py-6 md:py-8 lg:py-12 space-y-6 md:space-y-8 lg:space-y-12">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-foreground hover:text-primary hover:glow-primary-hover transition-all duration-300 hover:scale-105 hover:bg-primary/5"
            >
              <Icon name="ArrowLeft" size={20} />
              <span>Voltar</span>
            </button>
          </div>
          <div className="text-center space-y-3 md:space-y-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-primary/10 flex items-center justify-center shadow-md">
                <Icon name="Trophy" size={32} color="var(--color-primary)" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-primary">
              Hall da Fama
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
              Os conteúdos mais bem avaliados e populares da UP MIDIA.\nDescubra as artes e vídeos que conquistaram nossos clientes.
            </p>
          </div>

          <StatsOverview stats={stats} />

          <FilterBar
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            totalCount={filteredContent?.length}
            activeFilter={selectedCategory}
            onFilterChange={setSelectedCategory}
            activeTimeFilter={sortBy}
            onTimeFilterChange={setSortBy}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {filteredContent?.map((content, index) => (
              <ContentCard
                key={content?.id}
                content={content}
                rank={index + 1}
                onWhatsAppRequest={handleWhatsAppRequest}
              />
            ))}
          </div>

          <LeaderboardSection
            leaderboardData={filteredContent}
            activeCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            activeType={selectedType}
            onTypeChange={setSelectedType}
            sortBy={sortBy}
            onSortByChange={setSortBy}
          />

          <div className="bg-card rounded-lg p-6 md:p-8 lg:p-12 text-center shadow-lg">
            <div className="max-w-2xl mx-auto space-y-4 md:space-y-6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                Gostou do que viu?
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
                Entre em contato conosco e solicite conteúdos personalizados para seu projeto.\nQualidade premium garantida pela UP MIDIA.
              </p>
              <button
                onClick={() => {
                  const message = 'Olá UP MIDIA! Vi o Hall da Fama e gostaria de solicitar um conteúdo personalizado. Aguardo retorno!';
                  const whatsappUrl = `https://wa.me/5511999999999?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="inline-flex items-center gap-3 px-6 py-3 md:px-8 md:py-4 bg-secondary text-secondary-foreground rounded-md font-heading font-bold text-base md:text-lg transition-all hover:bg-secondary/90 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
              >
                <Icon name="MessageCircle" size={24} />
                <span>Solicitar Conteúdo</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HallOfFame;